import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import { useSelector, useDispatch } from 'react-redux';
import { getPolicies, getTasks } from '../redux/policiesReducer';
import request from '../helpers/request';
import { getPresignedUrl } from '../helpers/storage';
import TaskReportsTable from '../components/TaskReportsTable';
import History from "../components/History";
import { toast } from "react-toastify";
import SplitscreenOutlinedIcon from '@mui/icons-material/SplitscreenOutlined';

const TasksPage = () => {
    const dispatch = useDispatch();
    const { isPoliciesLoading, policies, pages } = useSelector(state => state.policies);

    const [startTask, setStartTask] = useState(false);
    const [currentPolicy, setCurrentPolicy] = useState({});
    const [currentOutputPolicy, setCurrentOutputPolicy] = useState({});
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [currentPage, setCurrentPage] = useState({});
    // const [apiKey, setApiKey] = useState({});
    const [referencedObjects, setReferencedObjects] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(getPolicies());
        dispatch(getTasks());
        // getApiKey();
    }, [dispatch]);

    useEffect(() => {
        if (pages.length > 0 && Object.keys(currentPage).length === 0) {
            setCurrentPage(pages[0]);
        }
    }, [pages, currentPage]);

    const refreshTable = () => dispatch(getTasks());

    //-------Returns 404, what is this for?-----
    // const getApiKey = async () => {
    //     const response = await request.get('/api/v2/knowledgebase/api-key/');
    //     const currentKey = response.data.find(key => key.is_active) || {};
    //     setApiKey(currentKey);
    // };

    const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
    const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpeg', '.jpg'];

    const handleFileChange = (event) => {
        const newFiles = Array.from(event.target.files);
        const filtered = [];
        const rejectedFiles = [];
        newFiles.forEach(file => {
            if (
                ACCEPTED_TYPES.includes(file.type) ||
                ACCEPTED_EXTENSIONS.includes('.' + file.name.toLowerCase().split('.').pop())
            ) {
                filtered.push(file);
            } else {
                rejectedFiles.push(file.name);
            }
        });
        if (rejectedFiles.length > 0) {
            toast.error(`Invalid file type(s): ${rejectedFiles.join(', ')}`);
        }
        if (filtered.length) setSelectedFiles(prev => [...prev, ...filtered]);
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Validate inputs
            if (!currentPolicy || Object.keys(currentPolicy).length === 0) {
                throw new Error("Please choose a policy");
            }
            if (selectedFiles.length === 0) {
                throw new Error("Please select at least one document");
            }

            // Process each file
            for (const file of selectedFiles) {
                const fileReferences = referencedObjects[file.name] || {};

                // Validate references
                if (currentPolicy?.policy?.ReferencedObjects?.length > 0) {
                    for (const field of currentPolicy.policy.ReferencedObjects) {
                        if (!fileReferences[field.key]) {
                            throw new Error(`Please fill all reference data for ${file.name} - missing "${field.key}"`);
                        }
                    }
                }

                let documentBase64;
                if (file.isEvaluationDocument) {
                    // Fetch the document from URL and convert to base64
                    let fetchUrl = file.url;

                    // Check if we need to refresh the URL (if it's a path or expired signed URL)
                    // 1. If it's a path (doesn't start with http/blob)
                    // 2. If it has a path/s3Key property
                    // 3. If it's a signed URL (contains X-Amz-Signature)
                    const isPathUrl = file.url && !file.url.startsWith('http') && !file.url.startsWith('blob:');
                    const hasPath = file.path || file.s3Key;
                    const isSignedUrl = file.url && file.url.includes('X-Amz-Signature');

                    if (isPathUrl || hasPath || isSignedUrl) {
                        try {
                            // Determine the key
                            let key = file.path || file.s3Key;

                            // If no explicit key, try to use url if it looks like a path
                            if (!key && isPathUrl) {
                                key = file.url;
                            }

                            // If still no key and it's a signed URL, try to extract from URL
                            if (!key && isSignedUrl) {
                                try {
                                    const urlObj = new URL(file.url);
                                    const path = urlObj.pathname;
                                    // Assuming path contains user_uploads
                                    const marker = '/user_uploads/';
                                    const index = path.indexOf(marker);
                                    if (index !== -1) {
                                        key = path.substring(index + 1); // Extract from user_uploads/...
                                    }
                                } catch (e) {
                                    // Ignore URL parsing errors
                                }
                            }

                            if (key) {
                                const { data } = await getPresignedUrl('download', key);
                                fetchUrl = data.url;
                            }
                        } catch (e) {
                            console.warn("Failed to refresh presigned URL, using original", e);
                        }
                    }

                    const response = await fetch(fetchUrl);
                    const blob = await response.blob();
                    documentBase64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = () => resolve(reader.result.split(',')[1]);
                        reader.onerror = (error) => reject(error);
                    });
                } else {
                    documentBase64 = await convertToBase64(file);
                }

                // Prepare request body
                const taskType = currentPolicy?.settings?.type?.toLowerCase() === 'evaluation' ? 'evaluation' : 'compliance';
                const body = {
                    type: taskType,
                    payload: {
                        policy_id: currentPolicy.id,
                        objectReference: fileReferences,
                    },
                    document: documentBase64,
                };

                // Add policy output if exists
                if (currentOutputPolicy && Object.keys(currentOutputPolicy).length > 0) {
                    body.payload["policy_output_id"] = currentOutputPolicy.id;
                }

                await request.post("/api/v2/tools/task/", body);
            }

            setStartTask(false);
            setSelectedFiles([]);
            setReferencedObjects({});
            setCurrentPolicy({});
            setCurrentOutputPolicy({});
            refreshTable();
        } catch (error) {
            console.error("Error submitting policy:", error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Stack sx={{ px: { xs: 2, md: 4 }, pt: '0.5%', flexShrink: 0 }}>
                <Box sx={{ mb: 1 }}>
                    <History />
                </Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 'lg',
                            background: (theme) =>
                                `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'sm',
                        }}
                    >
                        <SplitscreenOutlinedIcon sx={{ fontSize: 28, color: 'white' }} />
                    </Box>
                    <Typography level="h2" component="h1">Tasks</Typography>
                </Stack>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', px: { xs: 2, md: 4 }, pt: 1, pb: 2 }}>
                <TaskReportsTable
                    startTask={startTask}
                    setStartTask={setStartTask}
                    loading={loading}
                    currentPolicy={currentPolicy}
                    setCurrentPolicy={setCurrentPolicy}
                    currentOutputPolicy={currentOutputPolicy}
                    setCurrentOutputPolicy={setCurrentOutputPolicy}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    handleFileChange={handleFileChange}
                    handleRemoveFile={handleRemoveFile}
                    handleSubmit={handleSubmit}
                    referencedObjects={referencedObjects}
                    setReferencedObjects={setReferencedObjects}
                    policiesFromProps={policies}
                    pages={pages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    isPoliciesLoading={isPoliciesLoading}
                    refreshTable={refreshTable}
                />
            </Box>
        </Box>
    );
};

export default TasksPage;
